import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const accentColor = '#00d9ff';

const galleryImageUrls = [
  "https://public.readdy.ai/ai/img_res/68108a5325fd9fdd80c0358076258c5a.jpg",
  "https://readdy.ai/api/search-image?query=AI%20music%20video%20cinematic%20abstract%20liquid%20motion%20neon%20cyan%20turquoise%20glowing%20particles%20flowing%20energy%20dark%20background%20ethereal%20dreamy%20music%20production%20visual&width=800&height=500&seq=mv02&orientation=landscape",
  "https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/40c040873765382c90dde45ff2889c68.jpeg",
  "https://readdy.ai/api/search-image?query=music%20production%20studio%20AI%20holographic%20interface%20neon%20cyan%20glowing%20sound%20waves%20frequency%20visualization%20dark%20background%20futuristic%20technology%20professional%20music%20creation&width=800&height=500&seq=mv04&orientation=landscape",
  "https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/d87e3f46fbd5b5824f82a9d17c4fe52a.jpeg",
  "https://readdy.ai/api/search-image?query=electronic%20music%20concert%20immersive%20visual%20experience%20neon%20cyan%20blue%20laser%20lights%20audience%20dark%20atmospheric%20stage%20performance%20spectacular%20light%20show%20futuristic%20show&width=800&height=500&seq=mv06&orientation=landscape",
];

const featureIcons = ['ri-music-2-line', 'ri-user-star-line', 'ri-film-line', 'ri-shield-check-line'];

export default function MusicMVPage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'zh' ? 'en' : 'zh');
  };

  const galleryKeys = ['mv.gallery.img1', 'mv.gallery.img2', 'mv.gallery.img3', 'mv.gallery.img4', 'mv.gallery.img5', 'mv.gallery.img6'];
  const featureKeys = [
    { titleKey: 'mv.feature1.title', descKey: 'mv.feature1.desc' },
    { titleKey: 'mv.feature2.title', descKey: 'mv.feature2.desc' },
    { titleKey: 'mv.feature3.title', descKey: 'mv.feature3.desc' },
    { titleKey: 'mv.feature4.title', descKey: 'mv.feature4.desc' },
  ];
  const stepKeys = [
    { num: '01', titleKey: 'mv.step1.title', descKey: 'mv.step1.desc' },
    { num: '02', titleKey: 'mv.step2.title', descKey: 'mv.step2.desc' },
    { num: '03', titleKey: 'mv.step3.title', descKey: 'mv.step3.desc' },
    { num: '04', titleKey: 'mv.step4.title', descKey: 'mv.step4.desc' },
  ];

  return (
    <div className="min-h-screen bg-[#00080d] text-white font-['Space_Grotesk']">
      {/* Top Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#00080d]/90 backdrop-blur-md border-b border-[#00d9ff]/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group cursor-pointer">
            <img
              src="https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/8a76a6dd2c1eb78247ce2c779019b5d9.png"
              alt="Pixel Monster Logo"
              className="w-10 h-10 object-contain"
            />
            <div className="flex flex-col">
              <span className="text-base font-bold text-white font-['Space_Grotesk']">像素怪兽</span>
              <span className="text-xs font-['JetBrains_Mono']" style={{ color: accentColor }}>PIXEL MONSTER</span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleLang}
              className="px-3 py-1 text-xs font-['JetBrains_Mono'] border transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
              style={{ borderColor: accentColor, color: accentColor, background: `${accentColor}15` }}
            >
              {t('nav.langToggle')}
            </button>
            <Link
              to="/"
              className="flex items-center gap-2 text-sm font-['JetBrains_Mono'] text-gray-400 hover:text-[#00d9ff] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              {t('service.backHome')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{ backgroundImage: `repeating-linear-gradient(0deg, ${accentColor} 0px, ${accentColor} 1px, transparent 1px, transparent 60px), repeating-linear-gradient(90deg, ${accentColor} 0px, ${accentColor} 1px, transparent 1px, transparent 60px)` }}
        />
        <div className="absolute top-20 left-0 w-[600px] h-[600px] rounded-full blur-[180px] opacity-10" style={{ background: accentColor }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 border text-xs font-['JetBrains_Mono'] font-semibold mb-8"
            style={{ borderColor: accentColor, color: accentColor, background: `${accentColor}15` }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: accentColor }}></span>
            {t('mv.badge')}
          </div>
          <h1 className="text-6xl md:text-8xl font-black mb-6 leading-none tracking-tight">
            <span className="text-white">{t('mv.h1.line1')}</span>
            <br />
            <span style={{ color: accentColor }}>{t('mv.h1.line2')}</span>
          </h1>
          <p className="text-xl text-gray-400 font-['JetBrains_Mono'] mb-4 max-w-2xl leading-relaxed">
            {t('mv.tagline')}
          </p>
          <p className="text-base text-gray-300 max-w-2xl leading-relaxed mb-12">
            {t('mv.intro')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {(['mv.stat1', 'mv.stat2', 'mv.stat3', 'mv.stat4'] as const).map((key, i) => (
              <div key={i} className="border border-gray-700/50 p-6"
                style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}>
                <div className="text-3xl font-black mb-1" style={{ color: accentColor }}>{t(`${key}.num`)}</div>
                <div className="text-xs text-gray-400 font-['JetBrains_Mono']">{t(`${key}.label`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-[#000508]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-xs font-['JetBrains_Mono'] mb-3" style={{ color: accentColor }}>{t('mv.gallery.tag')}</div>
            <h2 className="text-4xl font-bold text-white">{t('mv.gallery.title')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryImageUrls.map((url, i) => (
              <div key={i} className="group relative overflow-hidden cursor-pointer"
                style={{ clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))' }}
              >
                <div className="w-full h-56 overflow-hidden">
                  <img src={url} alt={t(galleryKeys[i])} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <p className="text-sm font-['JetBrains_Mono']" style={{ color: accentColor }}>{t(galleryKeys[i])}</p>
                </div>
                <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ borderColor: accentColor }}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -inset-4 blur-2xl opacity-20 rounded-full" style={{ background: accentColor }}></div>
              <div className="relative w-full h-96 overflow-hidden"
                style={{ clipPath: 'polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 24px 100%, 0 calc(100% - 24px))' }}>
                <img
                  src="https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/86c97ecb26c48c971cdd6814c57e4189.jpeg"
                  alt="Virtual Idol Stage"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accentColor}15, transparent)` }}></div>
              </div>
              <div className="absolute bottom-4 right-4 px-3 py-2 bg-black/80 border text-xs font-['JetBrains_Mono']"
                style={{ borderColor: accentColor, color: accentColor }}>
                {t('mv.imgTag')}
              </div>
            </div>
            <div>
              <div className="text-xs font-['JetBrains_Mono'] mb-3" style={{ color: accentColor }}>{t('mv.features.tag')}</div>
              <h2 className="text-4xl font-bold text-white mb-8">
                {t('mv.features.title1')}<br />
                <span style={{ color: accentColor }}>{t('mv.features.title2')}</span>
              </h2>
              <div className="space-y-5">
                {featureKeys.map((f, i) => (
                  <div key={i} className="flex gap-4 p-4 border border-gray-800 hover:border-[#00d9ff]/40 transition-colors"
                    style={{ clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))' }}>
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ background: `${accentColor}15` }}>
                      <i className={`${featureIcons[i]} text-xl`} style={{ color: accentColor }}></i>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white mb-1">{t(f.titleKey)}</div>
                      <div className="text-xs text-gray-400 leading-relaxed">{t(f.descKey)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#000508]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="text-xs font-['JetBrains_Mono'] mb-3" style={{ color: accentColor }}>{t('mv.process.tag')}</div>
            <h2 className="text-4xl font-bold text-white">{t('mv.process.title')}</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {stepKeys.map((s, i) => (
              <div key={i} className="relative p-6 border border-gray-700/60 hover:border-[#00d9ff]/60 transition-colors"
                style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}>
                <div className="text-5xl font-black mb-4 opacity-20" style={{ color: accentColor }}>{s.num}</div>
                <div className="text-base font-bold text-white mb-2">{t(s.titleKey)}</div>
                <div className="text-xs text-gray-400 leading-relaxed">{t(s.descKey)}</div>
                {i < stepKeys.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 z-10 w-6 h-[2px]" style={{ background: accentColor }}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ background: `radial-gradient(ellipse at center, ${accentColor}, transparent)` }}></div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs font-['JetBrains_Mono'] mb-4" style={{ color: accentColor }}>{t('mv.cta.tag')}</div>
          <h2 className="text-5xl font-black text-white mb-6">
            {t('mv.cta.title1')}<br />
            <span style={{ color: accentColor }}>{t('mv.cta.title2')}</span>
          </h2>
          <p className="text-gray-400 mb-10 font-['JetBrains_Mono'] text-sm">{t('mv.cta.desc')}</p>
          <Link
            to="/"
            onClick={() => setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 300)}
            className="inline-flex items-center gap-3 px-10 py-4 font-bold text-black transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{ background: accentColor, clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}
          >
            <i className="ri-send-plane-line"></i>
            {t('mv.cta.btn')}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/8a76a6dd2c1eb78247ce2c779019b5d9.png" alt="Logo" className="w-7 h-7 object-contain" />
            <span className="text-xs font-['JetBrains_Mono'] text-gray-500">© 2026 PIXEL MONSTER · All Rights Reserved</span>
          </div>
          <Link to="/" className="text-xs font-['JetBrains_Mono'] text-gray-500 hover:text-[#00d9ff] transition-colors cursor-pointer">{t('service.backHome')} →</Link>
        </div>
      </footer>
    </div>
  );
}
